import React, { useState, useCallback } from 'react';
import { Box, Typography, IconButton, Modal, Button, Pagination } from '@mui/material';
import { motion } from 'framer-motion';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';

const GALLERY_IMAGES = [
  '/beyond/gallery/05.jpg',
  '/beyond/gallery/06.jpg',
  '/beyond/gallery/FB_IMG_1519293504235.jpg',
  '/beyond/gallery/fggdrv.jpg',
  '/beyond/gallery/IMG-20190111-WA0021.jpg',
  '/beyond/gallery/IMG_1059.jpg',
  '/beyond/gallery/IMG_1082.jpg',
  '/beyond/gallery/IMG_1091.jpg',
  '/beyond/gallery/IMG_1129.jpg',
  '/beyond/gallery/IMG_1134.jpg',
  '/beyond/gallery/IMG_1144%20%281%29.jpg',
  '/beyond/gallery/IMG_1151.jpg',
  '/beyond/gallery/IMG_20180217_144340.jpg',
  '/beyond/gallery/IMG_20180217_162907.jpg',
  '/beyond/gallery/IMG_20180217_164323_Bokeh.jpg',
  '/beyond/gallery/IMG_20180217_164748.jpg',
  '/beyond/gallery/IMG_20190111_160345.jpg',
  '/beyond/gallery/IMG_2526.jpg',
  '/beyond/gallery/IMG_2530.jpg',
  '/beyond/gallery/IMG_2541.jpg',
  '/beyond/gallery/IMG_2543.jpg',
  '/beyond/gallery/IMG_2766.JPG',
  '/beyond/gallery/IMG_2769.JPG',
  '/beyond/gallery/IMG_2776.JPG',
  '/beyond/gallery/IMG_5869.JPG',
  '/beyond/gallery/IMG_6006.JPG',
  '/beyond/gallery/IMG_6832.jpg',
  '/beyond/gallery/RevIn_Club200715.jpg',
  '/beyond/gallery/vsff.jpg',
  '/beyond/gallery/WhatsApp%20Image%202024-05-09%20at%203.43.10%20PM.jpeg',
  '/beyond/gallery/WhatsApp%20Image%202024-05-09%20at%203.43.11%20PM.jpeg',
  '/beyond/gallery/WhatsApp%20Image%202024-05-09%20at%203.43.13%20PM.jpeg',
  '/beyond/gallery/WhatsApp%20Image%202024-05-09%20at%203.43.15%20PM.jpeg',
];

function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const BeyondGallery: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isFullGalleryPage = location.pathname === '/beyond-gallery';
  const [shuffledImages, setShuffledImages] = useState<string[]>(() =>
    shuffleArray(GALLERY_IMAGES)
  );
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const PREVIEW_COUNT = 9;
  const FULL_PAGE_SIZE = 12;

  const pageCount = Math.ceil(shuffledImages.length / FULL_PAGE_SIZE);
  const paginatedImages = shuffledImages.slice((page - 1) * FULL_PAGE_SIZE, page * FULL_PAGE_SIZE);
  const previewImages = shuffledImages.slice(0, PREVIEW_COUNT);
  const imagesToRender = isFullGalleryPage ? paginatedImages : previewImages;

  const handleShuffle = useCallback(() => {
    setShuffledImages((prev) => shuffleArray(prev));
    setPage(1);
  }, []);

  const handleOpenPopup = useCallback((src: string) => setSelectedImage(src), []);
  const handleClosePopup = useCallback(() => setSelectedImage(null), []);

  return (
    <Box
      id="beyond-gallery"
      sx={{
        padding: { xs: '48px 0', md: '64px 0' },
        background: '#000',
        color: '#fff',
      }}
    >
      <Box
        sx={{
          width: '85%',
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: { xs: '1.75rem', md: '2rem' },
              fontWeight: 500,
              color: '#fff',
            }}
          >
            Beyond Gallery
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={handleShuffle}
              sx={{
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.4)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.1)',
                  borderColor: 'rgba(255,255,255,0.7)',
                },
              }}
              aria-label="Shuffle gallery"
            >
              <ShuffleIcon />
            </IconButton>
            <Button
              onClick={() => navigate('/beyond-gallery')}
              variant="outlined"
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.45)',
                textTransform: 'none',
                borderRadius: '999px',
                fontWeight: 600,
                px: 2,
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.8)',
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              View Full Gallery
            </Button>
          </Box>
        </Box>

        <Typography sx={{ color: 'rgba(255,255,255,0.75)', mb: 2.5, maxWidth: 640, fontSize: '0.95rem' }}>
          {isFullGalleryPage
            ? 'Browse all gallery images page by page.'
            : 'A curated glimpse from our activities and student moments. Open full gallery to explore all highlights.'}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {imagesToRender.map((src, index) => (
            <motion.div
              key={src}
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ duration: 0 }}
              style={{
                borderRadius: 12,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
              onClick={() => handleOpenPopup(src)}
            >
                <Box
                  component="img"
                  src={src}
                  alt={`Gallery ${index + 1}`}
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: 240,
                    objectFit: 'cover',
                    objectPosition: 'top',
                    display: 'block',
                    borderRadius: '12px',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.03)',
                    },
                  }}
                />
            </motion.div>
          ))}
        </Box>

        {isFullGalleryPage ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.25)',
                },
                '& .Mui-selected': {
                  bgcolor: '#2563EB !important',
                  color: '#fff',
                },
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              onClick={() => navigate('/beyond-gallery')}
              variant="contained"
              sx={{
                textTransform: 'none',
                borderRadius: '999px',
                fontWeight: 700,
                px: 3.5,
                py: 1.1,
                bgcolor: '#2563EB',
                '&:hover': { bgcolor: '#1D4ED8' },
              }}
            >
              Explore Complete Gallery
            </Button>
          </Box>
        )}
      </Box>

      {/* Image popup / lightbox */}
      <Modal
        open={!!selectedImage}
        onClose={handleClosePopup}
        slotProps={{
          backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.92)' } },
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            outline: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            onClick={handleClosePopup}
            sx={{
              position: 'fixed',
              top: 24,
              right: 24,
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.15)',
              zIndex: 1400,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.25)',
              },
            }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Gallery view"
              sx={{
                maxWidth: '95vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default BeyondGallery;
